/**
 * Give every maneuver type its own STATE filter memory.
 *
 * ROUNDUP / BEARAWAY, TACK / GYBE and TAKEOFF have entirely disjoint state vocabularies (tack
 * geometry vs the H-ladder vs the takeoff types — see maneuverStatePersistence), so a selection
 * carried across a type change, or
 * restored from one shared saved list, names states that cannot exist on the new view. The symptom
 * is a table with no rows and no visible cause: the filter looks set, but nothing can ever match it.
 *
 * This hook is the single choke point that owns the swap. On every event-type change it loads the
 * INCOMING type's own saved chips and applies them; the outgoing type's chips were already written
 * when the user set them (persistStates hangs off the page's toggleStateFilter), so saving is
 * deliberately NOT part of the switch — saving here would risk writing the old chips under the new
 * type's key.
 *
 * RaceSight discovers each type's vocabulary from the data rather than declaring it (see
 * maneuverStateOptionsService), so this hook does not reconcile. It restores, and reports through
 * `setHasSavedStates` whether the incoming type has a remembered selection at all. That flag is what
 * the pages' existing option-discovery step reads: with no remembered selection it selects every
 * state the type actually has, which is always matchable; with one it narrows the remembered list to
 * what the type has and leaves it alone otherwise.
 */

import { createEffect, on, onCleanup } from 'solid-js';
import { normalizeManeuverState } from './maneuverFoilingState';
import {
  loadLegacyFlatManeuverStates,
  loadManeuverStatesForEventType,
  saveManeuverStatesForEventType,
} from './maneuverStatePersistence';

/** Debounce for the per-type save: toggling four chips is one write, not four. */
const STATE_SAVE_DEBOUNCE_MS = 400;

/**
 * The event type a share link is about to select.
 *
 * Opening a link changes the type from the store default (TACK) to whatever was shared. That looks
 * like a user switching types, which would load the viewer's own saved chips over the shared ones.
 * The apply path sets this first; the matching restore consumes it and leaves the shared chips.
 */
let sharedEventTypeHold: string | null = null;

export function holdManeuverStateRestoreForShare(eventType: string): void {
  const evt = normalizeManeuverState(eventType);
  sharedEventTypeHold = evt || null;
}

/** True once, when `eventType` is the type the share just selected. */
export function takeSharedManeuverStateHold(eventType: string): boolean {
  const evt = normalizeManeuverState(eventType);
  if (!sharedEventTypeHold || sharedEventTypeHold !== evt) { return false; }
  sharedEventTypeHold = null;
  return true;
}

export interface ManeuverStatesForEventTypeOptions {
  /** The maneuver type on screen (TACK / GYBE / ROUNDUP / BEARAWAY / TAKEOFF). */
  eventType: () => string;
  /** The page's current State selection. */
  selectedStates: () => string[];
  /** Write a new selection to BOTH the page signal and the filterStore relay. */
  applySelection: (next: string[]) => void;
  /**
   * Record whether the incoming type has a remembered selection. The pages hold this as
   * `hasSavedStateFilter` and pass it to reconcileManeuverStatesWithBaseline /
   * applyManeuverFoilingStateAfterFetch, where false means "select every discovered state".
   */
  setHasSavedStates: (value: boolean) => void;
  /** Current user id, or '' during init. */
  userId: () => string;
  /** Selected class name, or '' during init. */
  className: () => string;
  /** Selected project id, or 0 during init. */
  projectId: () => number;
  /**
   * When true, the FIRST restore is skipped and only real type changes act. Pages pass
   * `willRestoreShare(PAGE_KEY)` here: a shared link carries its own State chips, and this hook
   * would otherwise overwrite them at mount with whatever the viewer last used for that type.
   */
  suppressInitialRestore?: () => boolean;
}

export interface ManeuverStatesForEventTypeApi {
  /**
   * Persist the State chips as they stand, under the maneuver type on screen. Debounced, and a
   * no-op if the type changed between scheduling and flushing.
   */
  persistStates: () => void;
  /**
   * Apply the project's `maneuver_filters` default states — but ONLY when this user has never
   * filtered the maneuver type on screen.
   *
   * The project object holds one flat state list for every type, so it can only ever be a
   * first-visit seed. Letting it write unconditionally (which is what the mount init did) overwrites
   * the per-type chips this hook has just restored, on every reload.
   */
  applyProjectDefaultStates: (states: unknown) => Promise<void>;
}

export function useManeuverStatesForEventType(
  options: ManeuverStatesForEventTypeOptions
): ManeuverStatesForEventTypeApi {
  const {
    eventType,
    selectedStates,
    applySelection,
    setHasSavedStates,
    userId,
    className,
    projectId,
  } = options;

  /**
   * Bumped on every type change. A restore that finds the token moved has been overtaken by a later
   * switch and must not write — clicking TACK -> ROUNDUP -> BEARAWAY quickly has to settle on
   * BEARAWAY's set, not on whichever fetch happened to resolve last.
   */
  let restoreToken = 0;
  let saveDebounce: ReturnType<typeof setTimeout> | null = null;

  /**
   * The type the last restore ran for, or null before the first. Distinguishes the first run (the
   * page is still assembling its filters in onMount) from a genuine type change, which is the only
   * case allowed to throw the current chips away when nothing can be loaded to replace them.
   */
  let lastRestoredEventType: string | null = null;

  /**
   * The type applyProjectDefaultStates last seeded, cleared whenever the type changes. A restore
   * that finds no per-type memory must leave that seed standing rather than overwriting it with the
   * legacy flat blob — the project default is the stronger of the two seeds, and the two land in a
   * non-deterministic order during mount.
   */
  let projectSeededEventType: string | null = null;

  /** null whenever any of the three is missing, which means "cannot persist, use defaults". */
  const scope = (): { userId: string; className: string; projectId: number } | null => {
    const uid = (userId() || '').trim();
    const cls = (className() || '').trim();
    const pid = Number(projectId());
    if (!uid || !cls || !pid) { return null; }
    return { userId: uid, className: cls, projectId: pid };
  };

  const sameSelection = (a: string[], b: string[]): boolean =>
    a.length === b.length && a.every((s, i) => s === b[i]);

  const applyIfChanged = (next: string[]): void => {
    if (sameSelection(next, selectedStates() || [])) { return; }
    applySelection(next);
  };

  const restore = async (rawEventType: string): Promise<void> => {
    const evt = normalizeManeuverState(rawEventType);
    restoreToken += 1;
    const token = restoreToken;
    if (evt === '') { return; }

    const typeChanged = lastRestoredEventType !== null && lastRestoredEventType !== evt;
    lastRestoredEventType = evt;
    if (typeChanged) { projectSeededEventType = null; }
    // A share link carries its own chips. Consuming the hold covers the type change from the
    // store default to the shared type; the first-run suppress covers a link that was already
    // on that default. Either way the remembered per-type list must not replace the link.
    if (takeSharedManeuverStateHold(evt) || (!typeChanged && options.suppressInitialRestore?.())) {
      setHasSavedStates(true);
      return;
    }

    // A pending save belongs to the type we are leaving; it read that type's chips at schedule time,
    // but flushing it now would read the NEW type from eventType(). Drop it — the outgoing type was
    // persisted when the user toggled, which is the whole point of the debounce.
    if (saveDebounce) {
      clearTimeout(saveDebounce);
      saveDebounce = null;
    }

    const stale = (): boolean =>
      token !== restoreToken || normalizeManeuverState(eventType()) !== evt;

    const target = scope();
    if (!target) {
      // No user / class / project yet, so nothing can be loaded. On a real type change the outgoing
      // type's chips still must not stand over the incoming type — they are from another vocabulary
      // — so clear them and hand the choice to option discovery. On the first run there is nothing
      // to clear and the page is still assembling its own filters; leave it alone.
      if (!typeChanged) { return; }
      setHasSavedStates(false);
      applyIfChanged([]);
      return;
    }

    const saved = await loadManeuverStatesForEventType(
      target.userId, target.className, target.projectId, evt
    );
    if (stale()) { return; }

    if (saved !== null) {
      // A saved list may name states that have since left the vocabulary; the pages' option
      // discovery narrows it. An explicitly saved [] is a real "no state filter" and must survive
      // that step, which is what the true flag guarantees.
      setHasSavedStates(true);
      applyIfChanged(saved);
      return;
    }

    // Never filtered this type. Seed from the pre-per-type flat blob so an upgrading user does not
    // read this as "the app forgot my filter", and leave the flag false so option discovery drops
    // whatever the type cannot show and falls back to every state it has.
    const legacy = await loadLegacyFlatManeuverStates(
      target.userId, target.className, target.projectId
    );
    if (stale()) { return; }
    // The project default already seeded this type; it outranks the flat blob.
    if (projectSeededEventType === evt) { return; }

    setHasSavedStates(false);
    applyIfChanged(legacy ?? []);
  };

  const flushSave = (evt: string): void => {
    saveDebounce = null;
    // The type moved between scheduling and flushing; those chips are not this type's.
    if (normalizeManeuverState(eventType()) !== evt) { return; }
    const target = scope();
    if (!target) { return; }
    setHasSavedStates(true);
    void saveManeuverStatesForEventType(
      target.userId, target.className, target.projectId, evt, selectedStates() || []
    );
  };

  const persistStates = (): void => {
    const evt = normalizeManeuverState(eventType());
    if (evt === '') { return; }
    if (saveDebounce) { clearTimeout(saveDebounce); }
    saveDebounce = setTimeout(() => flushSave(evt), STATE_SAVE_DEBOUNCE_MS);
  };

  const applyProjectDefaultStates = async (states: unknown): Promise<void> => {
    const evt = normalizeManeuverState(eventType());
    if (evt === '' || !Array.isArray(states)) { return; }

    const target = scope();
    if (target) {
      const saved = await loadManeuverStatesForEventType(
        target.userId, target.className, target.projectId, evt
      );
      // This user has their own chips for this type — the project default is a seed, not an override.
      if (saved !== null) { return; }
      if (normalizeManeuverState(eventType()) !== evt) { return; }
    }

    // Not marked as saved: the project list is written for one vocabulary, so option discovery must
    // still be free to fall back to every state this type has when none of it applies here.
    setHasSavedStates(false);
    projectSeededEventType = evt;
    applyIfChanged((states as unknown[]).map((s) => normalizeManeuverState(s)).filter((s) => s !== ''));
  };

  onCleanup(() => {
    if (saveDebounce) {
      clearTimeout(saveDebounce);
      saveDebounce = null;
    }
  });

  // No `defer`: the first run is exactly the case this exists for — a page opening on TACK has to
  // come back with TACK's own chips, not with whatever the last-used type left in the flat blob.
  // The scope is tracked alongside the type because user / class / project all arrive after the
  // component is created, and a restore attempted before they land can only clear.
  createEffect(on(
    () => [
      normalizeManeuverState(eventType()),
      (userId() || '').trim(),
      (className() || '').trim(),
      Number(projectId()) || 0,
    ] as const,
    ([evt]) => { void restore(evt); }
  ));

  return { persistStates, applyProjectDefaultStates };
}
